import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Bell, Mail, MessageSquare, CheckSquare, Users, FileText, Save } from 'lucide-react';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    newClient: true,
    newTask: true,
    taskDue: true,
    newMessage: true,
    newInvoice: true,
    invoicePaid: true,
    teamUpdates: false,
    weeklyDigest: true
  });
  const { toast } = useToast();

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    toast({ title: 'Success', description: 'Notification settings saved' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Notification Settings</h1>
        <p className="text-gray-500 mt-1">Manage how you receive notifications</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>Receive email notifications for important updates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-2">
            <Label htmlFor="emailNotifications" className="cursor-pointer">
              Enable email notifications
            </Label>
            <Switch
              id="emailNotifications"
              checked={settings.emailNotifications}
              onCheckedChange={() => handleToggle('emailNotifications')}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Activity Notifications
          </CardTitle>
          <CardDescription>Choose which activities trigger notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="newClient" className="cursor-pointer">New Client Added</Label>
                <p className="text-sm text-gray-500">When a new client is created</p>
              </div>
            </div>
            <Switch
              id="newClient"
              checked={settings.newClient}
              onCheckedChange={() => handleToggle('newClient')}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="newTask" className="cursor-pointer">New Task Assigned</Label>
                <p className="text-sm text-gray-500">When a task is assigned to you</p>
              </div>
            </div>
            <Switch
              id="newTask"
              checked={settings.newTask}
              onCheckedChange={() => handleToggle('newTask')}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="taskDue" className="cursor-pointer">Task Due Reminder</Label>
                <p className="text-sm text-gray-500">Reminder before task due date</p>
              </div>
            </div>
            <Switch
              id="taskDue"
              checked={settings.taskDue}
              onCheckedChange={() => handleToggle('taskDue')}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="newMessage" className="cursor-pointer">New Message</Label>
                <p className="text-sm text-gray-500">When you receive a new message</p>
              </div>
            </div>
            <Switch
              id="newMessage"
              checked={settings.newMessage}
              onCheckedChange={() => handleToggle('newMessage')}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="newInvoice" className="cursor-pointer">New Invoice Created</Label>
                <p className="text-sm text-gray-500">When a new invoice is created</p>
              </div>
            </div>
            <Switch
              id="newInvoice"
              checked={settings.newInvoice}
              onCheckedChange={() => handleToggle('newInvoice')}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="invoicePaid" className="cursor-pointer">Invoice Paid</Label>
                <p className="text-sm text-gray-500">When an invoice is marked as paid</p>
              </div>
            </div>
            <Switch
              id="invoicePaid"
              checked={settings.invoicePaid}
              onCheckedChange={() => handleToggle('invoicePaid')}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="teamUpdates" className="cursor-pointer">Team Updates</Label>
                <p className="text-sm text-gray-500">When team members make changes</p>
              </div>
            </div>
            <Switch
              id="teamUpdates"
              checked={settings.teamUpdates}
              onCheckedChange={() => handleToggle('teamUpdates')}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Weekly Digest</CardTitle>
          <CardDescription>Receive a weekly summary of your activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-2">
            <Label htmlFor="weeklyDigest" className="cursor-pointer">
              Enable weekly digest email
            </Label>
            <Switch
              id="weeklyDigest"
              checked={settings.weeklyDigest}
              onCheckedChange={() => handleToggle('weeklyDigest')}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Save className="h-4 w-4" />
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;
